import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

import { Stack, Text } from '@fluentui/react';

import { ProfileRoleEnum } from '../../api-client';
import { RootState } from '../../redux/application/redux-store';
import { RouterSettings } from '../../utils/router';
import { TRANSLATION_KEYS } from './localization/translation-keys';
import { useClassNames } from './homepage.classNames';

import { IAIApplicationLayout } from '../../common/layouts/IAIApplicationLayout';
import { IAIWidgetIcon } from '../../common/components/IAIWidget';
import WidgetNotices from './components/WidgetNotices/widget-notices';

const Homepage: React.FunctionComponent = () => {
    const {
        homepageContainer
    } = useClassNames();

    const {
        t: translate
    } = useTranslation();

    const HOMEPAGE_WIDGETS = [
        {
            icon: 'Add',
            title: translate(TRANSLATION_KEYS.WidgetNewTicketHeading),
            tooltip: translate(TRANSLATION_KEYS.WidgetNewTicketTooltip),
            to: RouterSettings.PATHS.dashboard.ticket.new,
            roles: [
                ProfileRoleEnum.Administrator,
                ProfileRoleEnum.DepartmentAgent,
                ProfileRoleEnum.DepartmentManager,
                ProfileRoleEnum.User
            ]
        },
        {
            icon: 'Ticket',
            title: translate(TRANSLATION_KEYS.WidgetTicketHeading),
            tooltip: translate(TRANSLATION_KEYS.WidgetTicketTooltip),
            to: RouterSettings.PATHS.dashboard.ticket.root,
            roles: [
                ProfileRoleEnum.Administrator,
                ProfileRoleEnum.DepartmentAgent,
                ProfileRoleEnum.DepartmentManager,
                ProfileRoleEnum.User
            ]
        },
        {
            icon: 'Lightbulb',
            title: translate(TRANSLATION_KEYS.WidgetSolutionHeading),
            tooltip: translate(TRANSLATION_KEYS.WidgetSolutionTooltip),
            to: RouterSettings.PATHS.dashboard.solution.root,
            roles: [
                ProfileRoleEnum.Administrator,
                ProfileRoleEnum.DepartmentManager
            ]
        },
        {
            icon: 'LineChart',
            title: translate(TRANSLATION_KEYS.WidgetReportHeading),
            tooltip: translate(TRANSLATION_KEYS.WidgetReportTooltip),
            to: RouterSettings.PATHS.dashboard.reportStats.root,
            roles: [
                ProfileRoleEnum.Administrator,
                ProfileRoleEnum.DepartmentManager
            ]
        }
    ];

    const iaiAccountInfo = useSelector((state: RootState) => state.account);

    const _handleWidgetsByRole = () => (
        _.filter(HOMEPAGE_WIDGETS, entry => (
            _.some(entry.roles, entry => entry === iaiAccountInfo.role)
        ))
    );

    return (
        <IAIApplicationLayout
            icon={'Home'}
            heading={translate(TRANSLATION_KEYS.Heading)}
            secondaryHeading={translate(TRANSLATION_KEYS.SecondaryHeading)}
        >
            <div className={`mt-3 ${homepageContainer}`}>
                <div className="row">
                    <div className="col-12 col-md-6">
                        <Text block variant="xLargePlus" className="mb-4">
                            {translate(TRANSLATION_KEYS.WidgetSectionHeading)}
                        </Text>

                        <Stack
                            horizontal
                            horizontalAlign="center"
                            tokens={{ childrenGap: 15 }}
                            wrap
                            styles={{ inner: { justifyContent: 'start' } }}
                        >
                            {_.map(_handleWidgetsByRole(), (dataItem, dataIndex) => (
                                <IAIWidgetIcon key={dataIndex} {...dataItem} />
                            ))}
                        </Stack>
                    </div>
                    <div className="col-12 col-md-6">
                        <Text block variant="xLargePlus" className="mb-4">
                            {translate(TRANSLATION_KEYS.NoticeSectionHeading)}
                        </Text>

                        <WidgetNotices />
                    </div>
                </div>
            </div>
        </IAIApplicationLayout>
    );
}

export default Homepage;